import type { IStyleCollectionPayload } from '../types';

// CSS variable name patterns to collect
const VARIABLE_PATTERNS = [
  'color',
  'bg',
  'background',
  'font',
  'radius',
  'shadow',
  'border',
  'text',
  'primary',
  'secondary',
  'accent',
  'surface',
  'foreground',
  'muted',
];

const MAX_CSS_VARIABLES = 100;

/**
 * Check if a CSS variable name matches our collection patterns.
 */
function matchesPattern(name: string): boolean {
  const lowerName = name.toLowerCase();
  return VARIABLE_PATTERNS.some(pattern => lowerName.includes(pattern));
}

/**
 * Collect CSS custom properties from :root and body elements.
 */
export function collectCSSVariables(): Record<string, string> {
  const variables: Record<string, string> = {};
  let count = 0;

  try {
    // Get computed styles from :root (document.documentElement)
    const rootStyles = getComputedStyle(document.documentElement);

    // Get all CSS custom properties from stylesheets
    for (const sheet of document.styleSheets) {
      try {
        const rules = sheet.cssRules || sheet.rules;
        if (!rules) continue;

        for (const rule of rules) {
          if (rule instanceof CSSStyleRule) {
            // Check :root and body rules
            if (rule.selectorText === ':root' || rule.selectorText === 'body') {
              for (const prop of rule.style) {
                if (prop.startsWith('--') && matchesPattern(prop)) {
                  if (count >= MAX_CSS_VARIABLES) break;
                  const value = rule.style.getPropertyValue(prop).trim();
                  if (value) {
                    variables[prop] = value;
                    count++;
                  }
                }
              }
            }
          }
        }
      } catch {
        // Cross-origin stylesheets will throw, skip them
        continue;
      }
    }

    // Also check inline styles on :root
    for (let i = 0; i < rootStyles.length; i++) {
      const prop = rootStyles[i];
      if (prop.startsWith('--') && matchesPattern(prop)) {
        if (count >= MAX_CSS_VARIABLES) break;
        const value = rootStyles.getPropertyValue(prop).trim();
        if (value && !variables[prop]) {
          variables[prop] = value;
          count++;
        }
      }
    }
  } catch {
    // Silently fail if something goes wrong
  }

  return variables;
}

/**
 * Check if a color value is transparent or empty
 */
function isTransparentColor(color: string): boolean {
  if (!color) return true;
  const c = color.trim().toLowerCase();
  return (
    c === 'transparent' ||
    c === 'rgba(0, 0, 0, 0)' ||
    c === 'initial' ||
    c === 'inherit' ||
    (c.startsWith('rgba') && c.endsWith(', 0)'))
  );
}

/**
 * Parse RGB color string to components
 */
function parseRgbColor(color: string): { r: number; g: number; b: number } | null {
  const match = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return null;
  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10),
  };
}

/**
 * Check if a color is neutral (white, black, gray) - not a good primary color
 */
function isNeutralColor(color: string): boolean {
  const rgb = parseRgbColor(color);
  if (!rgb) return false;

  const { r, g, b } = rgb;

  // Check if it's very light (white-ish) - all channels > 240
  if (r > 240 && g > 240 && b > 240) return true;

  // Check if it's very dark (black-ish) - all channels < 30
  if (r < 30 && g < 30 && b < 30) return true;

  // Check if it's gray (all channels within 20 of each other)
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max - min < 20) return true;

  return false;
}

/**
 * Calculate color saturation (0-1) to prioritize vibrant colors
 */
function getColorSaturation(color: string): number {
  const rgb = parseRgbColor(color);
  if (!rgb) return 0;

  const { r, g, b } = rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  if (max === 0) return 0;
  return (max - min) / max;
}

/**
 * Get computed styles for a specific element.
 */
function getElementStyles(
  selectors: string[],
  properties: string[]
): Record<string, string> | undefined {
  for (const selector of selectors) {
    try {
      const element = document.querySelector(selector);
      if (element) {
        const styles = getComputedStyle(element);
        const result: Record<string, string> = {};

        for (const prop of properties) {
          const value = styles.getPropertyValue(prop);
          if (value) {
            // Convert kebab-case to camelCase for the result
            const camelProp = prop.replace(/-([a-z])/g, (_, letter) =>
              letter.toUpperCase()
            );
            result[camelProp] = value.trim();
          }
        }

        // Only return if we got all properties
        if (Object.keys(result).length === properties.length) {
          return result;
        }
      }
    } catch {
      // Skip if selector fails
      continue;
    }
  }
  return undefined;
}

/**
 * Find a button/CTA element with a visible, colorful background.
 * Prioritizes saturated colors over neutral ones (white, gray, black).
 */
function findVisibleButtonStyles(): Record<string, string> | undefined {
  // Selectors ordered by likelihood of being a primary CTA
  const ctaSelectors = [
    // Explicit CTA classes
    '[class*="cta"]',
    '[class*="primary"]',
    '[class*="btn-primary"]',
    '[class*="button-primary"]',
    // Common button patterns
    'a[class*="btn"]',
    'a[class*="button"]',
    'button[class*="btn"]',
    // Generic buttons
    'button',
    '[role="button"]',
    // Links that look like buttons
    'a.btn',
    'a.button',
  ];

  const properties = ['background-color', 'color', 'border-radius'];

  // Collect all candidates with their saturation
  interface ButtonCandidate {
    styles: Record<string, string>;
    saturation: number;
    isNeutral: boolean;
  }
  const candidates: ButtonCandidate[] = [];

  for (const selector of ctaSelectors) {
    try {
      const elements = document.querySelectorAll(selector);

      for (const element of elements) {
        const styles = getComputedStyle(element);
        const bgColor = styles.getPropertyValue('background-color').trim();

        // Skip transparent
        if (isTransparentColor(bgColor)) {
          continue;
        }

        const result: Record<string, string> = {};
        for (const prop of properties) {
          const value = styles.getPropertyValue(prop);
          if (value) {
            const camelProp = prop.replace(/-([a-z])/g, (_, letter) =>
              letter.toUpperCase()
            );
            result[camelProp] = value.trim();
          }
        }

        if (Object.keys(result).length > 0) {
          candidates.push({
            styles: result,
            saturation: getColorSaturation(bgColor),
            isNeutral: isNeutralColor(bgColor),
          });
        }
      }
    } catch {
      continue;
    }
  }

  // Sort: prefer non-neutral, then by saturation (higher is better)
  candidates.sort((a, b) => {
    // Non-neutral buttons first
    if (a.isNeutral !== b.isNeutral) {
      return a.isNeutral ? 1 : -1;
    }
    // Then by saturation (descending)
    return b.saturation - a.saturation;
  });

  return candidates[0]?.styles;
}

/**
 * Collect computed styles from anchor elements on the page.
 */
export function collectComputedStyles(): IStyleCollectionPayload['computedStyles'] {
  const result: IStyleCollectionPayload['computedStyles'] = {};

  try {
    // Body styles
    const bodyStyles = getElementStyles(
      ['body'],
      ['background-color', 'color', 'font-family']
    );
    if (bodyStyles) {
      result.body = bodyStyles as typeof result.body;
    }

    // Button styles - prioritize finding a button with visible background
    const buttonStyles =
      findVisibleButtonStyles() ||
      getElementStyles(
        ['button', '[role="button"]', '.btn-primary', '.btn', 'a.button'],
        ['background-color', 'color', 'border-radius']
      );
    if (buttonStyles) {
      result.button = buttonStyles as typeof result.button;
    }

    // Heading styles
    const headingStyles = getElementStyles(
      ['h1', 'h2', 'h3'],
      ['color', 'font-family', 'font-weight']
    );
    if (headingStyles) {
      result.heading = headingStyles as typeof result.heading;
    }

    // Input styles
    const inputStyles = getElementStyles(
      ['input', 'textarea', 'input[type="text"]', 'input[type="email"]'],
      ['border-color', 'border-radius', 'background-color']
    );
    if (inputStyles) {
      result.input = inputStyles as typeof result.input;
    }

    // Header styles
    const headerStyles = getElementStyles(
      ['[role="banner"]', 'header', 'nav'],
      ['background-color']
    );
    if (headerStyles) {
      result.header = headerStyles as typeof result.header;
    }
  } catch {
    // Silently fail
  }

  return result;
}

/**
 * Collect all style data from the host page.
 */
export function collectStyleData(): IStyleCollectionPayload {
  return {
    cssVariables: collectCSSVariables(),
    computedStyles: collectComputedStyles(),
  };
}
